import { useCallback, useState } from 'react';
import { Button } from '@/components/common';
import { PhoneInput } from '@/components/forms';
import { ReferidoNavigator } from './ReferidoNavigator';
import { sheetsService } from '@/services/sheets';
import { useAuth } from '@/context/AuthContext';
import type { ReferidoFormData } from '@/types';

interface PhasePhonesProps {
  referidos: ReferidoFormData[];
  currentIndex: number;
  onUpdateReferido: (index: number, updates: Partial<ReferidoFormData>) => void;
  onNavigate: (index: number) => void;
  onContinue: () => void;
  onBack: () => void;
}

export function PhasePhones({
  referidos,
  currentIndex,
  onUpdateReferido,
  onNavigate,
  onContinue,
  onBack,
}: PhasePhonesProps) {
  const [duplicateWarning, setDuplicateWarning] = useState<string | null>(null);
  const { user } = useAuth();
  const current = referidos[currentIndex];

  if (!current) return null;

  const filledCount = referidos.filter((r) => r.telefono?.length === 10).length;
  const allFilled = filledCount === referidos.length;

  const checkDuplicate = useCallback(
    async (telefono: string) => {
      if (!user?.email || telefono.length !== 10) {
        setDuplicateWarning(null);
        return;
      }

      // Revisa contra el Google Sheet compartido usando el email del vendedor
      // (mismo identificador que ya usan Historial y Dashboard).
      const dups = await sheetsService.checkDuplicatePhones(user.email, user.name || '', [telefono]);
      if (dups.length > 0) {
        setDuplicateWarning(`Este número ya está registrado: ${dups[0].nombre}`);
      } else {
        setDuplicateWarning(null);
      }
    },
    [user?.email, user?.name]
  );

  const handlePhoneChange = (value: string) => {
    onUpdateReferido(currentIndex, { telefono: value });
    checkDuplicate(value);
  };

  return (
    <div className="space-y-4">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-gray-900">
          Números de teléfono
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Ahora pide los números. {filledCount}/{referidos.length} completados
        </p>
      </div>

      {/* Navigator */}
      <ReferidoNavigator
        currentIndex={currentIndex}
        total={referidos.length}
        onPrev={() => {
          setDuplicateWarning(null);
          onNavigate(currentIndex - 1);
        }}
        onNext={() => {
          setDuplicateWarning(null);
          onNavigate(currentIndex + 1);
        }}
        currentName={current.nombre + (current.apellido ? ` ${current.apellido}` : '')}
      />

      {/* Phone input */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
        {/* Show message status badge */}
        {current.mensaje_enviado && (
          <div className="flex items-center gap-2">
            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
              {current.mensaje_enviado === 'SMS' && 'SMS enviado'}
              {current.mensaje_enviado === 'WHATSAPP' && 'WhatsApp enviado'}
              {current.mensaje_enviado === 'IMESSAGE' && 'iMessage enviado'}
              {current.mensaje_enviado === 'CITA_INSTANTANEA' && 'Llamada instantánea'}
            </span>
            {current.respuesta_referido && (
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${current.respuesta_referido === 'OK_GRACIAS'
                  ? 'bg-green-100 text-green-700'
                  : current.respuesta_referido === 'PREGUNTO_QUIEN_ES'
                    ? 'bg-yellow-100 text-yellow-700'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                {current.respuesta_referido === 'OK_GRACIAS' && 'OK, gracias'}
                {current.respuesta_referido === 'PREGUNTO_QUIEN_ES' && 'Preguntó quién es'}
                {current.respuesta_referido === 'NO_RESPONDIO' && 'No respondió'}
              </span>
            )}
          </div>
        )}

        <PhoneInput
          value={current.telefono || ''}
          onChange={handlePhoneChange}
          required
        />

        {duplicateWarning && (
          <p className="text-sm text-amber-600 flex items-center gap-1">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            {duplicateWarning}
          </p>
        )}

        {current.telefono?.length === 10 && !duplicateWarning && (
          <p className="text-sm text-green-600 flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Número válido
          </p>
        )}
      </div>

      {/* Quick list showing all referidos phone status */}
      <div className="space-y-1">
        {referidos.map((ref, idx) => (
          <button
            key={idx}
            type="button"
            onClick={() => {
              setDuplicateWarning(null);
              onNavigate(idx);
            }}
            className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${idx === currentIndex
                ? 'bg-primary-50 text-primary-700 font-medium'
                : 'hover:bg-gray-50 text-gray-600'
              }`}
          >
            <span>{ref.nombre}{ref.apellido ? ` ${ref.apellido}` : ''}</span>
            {ref.telefono?.length === 10 ? (
              <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <span className="text-xs text-gray-400">pendiente</span>
            )}
          </button>
        ))}
      </div>

      {/* Navigation buttons */}
      <div className="flex gap-3 pt-2">
        <Button variant="secondary" onClick={onBack} className="flex-1">
          Volver
        </Button>
        <Button
          onClick={onContinue}
          className="flex-1"
          disabled={!allFilled}
        >
          Continuar a Detalles
        </Button>
      </div>
    </div>
  );
}
